import { h, Component, cloneElement } from 'preact';
import style from './style';
import cx from 'classnames';

const KEYCODES = {
	LEFT_ARROW: 37,
	RIGHT_ARROW: 39
};

/**
 * A tabbed layout. Expects all children to be <Tab> components.
 */
export default class Tabs extends Component {
	state = {
		active: 0
	};

	/**
	 * Set a new active tab.
	 */
	setActive = (index) => {
		if (this.props.onChangeActive) this.props.onChangeActive(index);
		if (this.state.active !== index) this.setState({ active: index });
	};

	/**
	 * Set a new active tab relative to the current active tab
	 */
	offsetActive(offset) {
		let { active } = this.state,
			max = this.props.children.length - 1;
		active = Math.min(max, Math.max(0, active + offset));
		this.setActive(active);
	}

	/**
	 * Move left or right between tabs using arrow keys
	 */
	handleTabKeyDown = (e) => {
		if (e.keyCode === KEYCODES.LEFT_ARROW) {
			this.offsetActive(-1);
		}
		else if (e.keyCode === KEYCODES.RIGHT_ARROW) {
			this.offsetActive(1);
		}
	}

	componentWillMount() {
		if (typeof this.props.active !== 'undefined') {
			this.setActive(this.props.active);
		}
	}

	componentWillReceiveProps({ active }) {
		if (typeof active !== 'undefined' && this.state.active !== active) {
			this.setActive(active);
		}
	}

	/**
	 * Render <NavItem>, using the title prop from <Tab> as the text of a <NavItem>
	 */
	renderNavigation = (theme) => this.props.children.map((child, index) => {
		const isActive = index === this.state.active;
		if (child.attributes.hidden) return null;
		return (
			<NavItem
				class={cx(isActive && this.props.tabActiveClass, this.props.tabClass)}
				index={index}
				active={isActive}
				onClick={this.setActive}
				onKeyDown={this.handleTabKeyDown}
				theme={theme}
			>
				{child.attributes.title || `Tab ${index}`}
			</NavItem>
		);
	})

	render({ children, theme, ...props }, { active }) {
		// Clone active property onto children
		children = children.map((child, index) => (
			cloneElement(child, { active: active === index })
		));

		return (
			<div {...props} class={cx(props.class)}>
				<ul role="tablist" class={style.nav}>
					{this.renderNavigation(theme)}
				</ul>
				{children}
			</div>
		);
	}
}


/**
 * One tab in a tabbed layout. It will be hidden if active === false
 * The title prop is used as the text in the <Tabs> navigation
 *
 * Does not render children until first activation.
 */
export class Tab extends Component {
	render({ children, active, title, ...props }) {
		if (active) this.hasEverBeenActive = true;
		return (
			<div
				{...props}
				role="tabpanel"
				aria-hidden={!active}
				class={cx(style.tab, active && style.active, props.class)}
			>
				{ this.hasEverBeenActive && children }
			</div>
		);
	}
}


/**
 * A navigation item in the <Tabs> nav menu.
 */
export class NavItem extends Component {
	setAnchorRef = (r) => { this.anchor = r; }

	handleClick = () => {
		let { index, onClick } = this.props;
		onClick(index);
	};

	/**
	 * When the component updates, force a change in focus to focus the <a> element.
	 */
	componentWillUpdate(nextProps) {
		if (nextProps.active && this.props.active !== nextProps.active) {
			this.anchor.focus();
		}
	}

	render({ children, onKeyDown, active, class: className, theme }) {
		return (
			<li
				role="presentation"
				class={cx(style['nav-item'], active && style.active, className, style[theme])}
				onKeyDown={onKeyDown}
				onClick={this.handleClick}
			>
				<a
					role="tab"
					ref={this.setAnchorRef}
					aria-selected={String(Boolean(active))}
					tabIndex={active ? 0 : -1}
				>
					{children}
				</a>
			</li>
		);
	}
}
